import type { PetEvolutionStage, PetStatusEffect } from '../pet';
import type { Widen } from 'src/replicant/utils/types';
import RawCareItemData from '../data/CareItem.json';
import {
  CareItems,
  CareItem,
  FoodItem,
  InvitationItem,
  SnackItem,
  ToyItem,
  FirstAidItem,
} from '../data/CareItem.schema';
import { assertNever } from 'src/replicant/utils';
import { duration } from '../utils/duration';

export const CareItemData = RawCareItemData as CareItems;

export type {
  CareItem,
  FoodItem,
  ToyItem,
  SnackItem,
  InvitationItem,
  FirstAidItem,
};
export type CareItemID = CareItem['id'];
export type CareItemType = CareItem['type'];

export type CareActionType = 'bath' | 'toilet';
export type DuoItem = 'i_vowsCoffee' | 'i_ultimateVowsCoffee';

export const careItems = CareItemData.reduce(
  (acc, item) => {
    acc[item.id] = item;
    return acc;
  },
  {} as Record<CareItemID, CareItem>,
);

export const careTypeItems = CareItemData.reduce(
  (acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  },
  {} as Record<CareItemType, CareItem[]>,
);

export function getCareItemCureChance(
  careItem: Widen<CareItem>,
  statusEffect: PetStatusEffect,
  resolutionAttempts: number,
) {
  switch (statusEffect) {
    case 'sick': {
      return careItem.sick_cure_chance;
    }
    case 'dying':
      return careItem.dying_cure_chance;

    case 'toothDecay': {
      return careItem.tooth_decay_cure_chance;
    }
    case 'scared': {
      if (resolutionAttempts > 1) {
        return 1;
      }
      return resolutionAttempts > 0 ? 0.8 : 0.4;
    }
    default: {
      return 0;
    }
  }
}

export const getCareItems = (type: CareItemType, stage: PetEvolutionStage) => {
  const items: Widen<CareItem>[] = careTypeItems[type];
  return items.filter((item) => item.stages?.match(stage));
};

/**
 * Get the price of the growth capsule for given stage.
 * @param stage
 * @returns
 */
export function getGrowthCapsulePrice(stage: PetEvolutionStage) {
  switch (stage) {
    case 'baby':
      return 10;
    case 'kid':
      return 50;
    case 'youth':
      return 100;
    case 'adult':
      throw new Error('Cannot use growth capsule on an adult pet');
    default:
      assertNever(stage);
  }
}

export const growthCapsuleUnlockedLevel = 2;
